import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/features/auth';
import {
  useContractColumns,
  ContractsDataTable,
  ChangeStatusDialog,
  StartBillingPeriodDialog,
} from '../components';
import {
  useContractFilters,
  useContractsList,
  useContractMutations,
  useContractStatusMutations,
  useBillingPeriodMutations,
} from '../hooks';
import type { Contract } from '../types';

export function ContractsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  const { filters, updateFilter, updateFilters, resetFilters, setPage, setLimit, handleSort } = useContractFilters();

  // Data fetching
  const { data, isLoading, refetch } = useContractsList(filters);
  const contracts = data?.results ?? [];
  const totalCount = data?.meta?.total ?? 0;

  // Mutations
  const { deleteMutation } = useContractMutations();
  const { activateMutation, closeMutation, renewMutation, recalculateMutation } =
    useContractStatusMutations();
  const { stopBillingPeriodMutation } = useBillingPeriodMutations();

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = React.useState(false);
  const [billingDialogOpen, setBillingDialogOpen] = React.useState(false);
  const [stopBillingDialogOpen, setStopBillingDialogOpen] = React.useState(false);
  const [recalculateDialogOpen, setRecalculateDialogOpen] = React.useState(false);
  const [selectedContract, setSelectedContract] = React.useState<Contract | null>(null);
  const [statusType, setStatusType] = React.useState<'accept' | 'close' | 'new'>('accept');

  // Handlers
  const handleEdit = (contract: Contract) => {
    navigate(`/app/contracts/action/${contract.id}`);
  };

  const handleDelete = (contract: Contract) => {
    setSelectedContract(contract);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedContract) {
      await deleteMutation.mutateAsync(selectedContract.id);
      setDeleteDialogOpen(false);
      setSelectedContract(null);
    }
  };

  const handleActivate = (contract: Contract) => {
    setSelectedContract(contract);
    setStatusType('accept');
    setStatusDialogOpen(true);
  };

  const handleClose = (contract: Contract) => {
    setSelectedContract(contract);
    setStatusType('close');
    setStatusDialogOpen(true);
  };

  const handleRenew = (contract: Contract) => {
    setSelectedContract(contract);
    setStatusType('new');
    setStatusDialogOpen(true);
  };

  const confirmStatusChange = async () => {
    if (!selectedContract) return;

    switch (statusType) {
      case 'accept':
        await activateMutation.mutateAsync(selectedContract.id);
        break;
      case 'close':
        await closeMutation.mutateAsync(selectedContract.id);
        break;
      case 'new':
        await renewMutation.mutateAsync(selectedContract.id);
        break;
    }

    setStatusDialogOpen(false);
    setSelectedContract(null);
  };

  const handleStartBilling = (contract: Contract) => {
    setSelectedContract(contract);
    setBillingDialogOpen(true);
  };

  const handleStopBilling = (contract: Contract) => {
    setSelectedContract(contract);
    setStopBillingDialogOpen(true);
  };

  const confirmStopBilling = async () => {
    if (!selectedContract) return;
    await stopBillingPeriodMutation.mutateAsync(selectedContract.id);
    setStopBillingDialogOpen(false);
    setSelectedContract(null);
  };

  // Re-pricing rewrites stored task prices, so it is confirmed rather than fired
  // straight from the menu.
  const handleRecalculate = (contract: Contract) => {
    setSelectedContract(contract);
    setRecalculateDialogOpen(true);
  };

  const confirmRecalculate = async () => {
    if (!selectedContract) return;
    try {
      await recalculateMutation.mutateAsync(selectedContract.id);
    } finally {
      setRecalculateDialogOpen(false);
      setSelectedContract(null);
    }
  };

  // Slot in the title row the table portals its Columns/Filters buttons into.
  const [tableControlsEl, setTableControlsEl] = React.useState<HTMLDivElement | null>(null);

  const columns = useContractColumns({
    onEdit: handleEdit,
    onDelete: handleDelete,
    onActivate: handleActivate,
    onClose: handleClose,
    onRenew: handleRenew,
    onStartBilling: handleStartBilling,
    onStopBilling: handleStopBilling,
    onRecalculate: handleRecalculate,
    hasAccess,
    orderBy: filters.orderBy,
    order: filters.order,
    onSort: handleSort,
  });

  return (
    <div>
      <div className="flex py-2 px-4 justify-between items-center border-b">
        <h1 className="text-xl font-semibold">{t('contracts')}</h1>
        <div className="flex items-center gap-3">
          <div ref={setTableControlsEl} className="flex items-center gap-2" />
          {hasAccess('contract-practice-area', 'get') && (
            <Link to="/app/settings/contract-practice-areas">
              <Button variant="outline">{t('contractPracticeArea')}</Button>
            </Link>
          )}
          {hasAccess('contract-period', 'get') && (
            <Link to="/app/settings/contract-periods">
              <Button variant="outline">{t('contractPeriods')}</Button>
            </Link>
          )}
          {hasAccess('contract', 'create') && (
            <Button onClick={() => navigate('/app/contracts/action')}>
              {t('new')} {t('contract')}
            </Button>
          )}
        </div>
      </div>

      <div className="px-4 pt-4">
        <ContractsDataTable
          controlsContainer={tableControlsEl}
          data={contracts}
          columns={columns}
          isLoading={isLoading}
          filters={filters}
          updateFilter={updateFilter}
          updateFilters={updateFilters}
          onClearFilters={resetFilters}
          onPageChange={setPage}
          onLimitChange={setLimit}
          totalCount={totalCount}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('areYouSure')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteContractConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? t('deleting') : t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Status Change Dialog */}
      <ChangeStatusDialog
        open={statusDialogOpen}
        onOpenChange={setStatusDialogOpen}
        type={statusType}
        onConfirm={confirmStatusChange}
        isLoading={
          activateMutation.isPending || closeMutation.isPending || renewMutation.isPending
        }
      />

      {/* Start Billing Period Dialog */}
      {/* Tariff fields prefill from the contract's own package inside the
          dialog — the template dropdown only prefills, never connects. */}
      <StartBillingPeriodDialog
        open={billingDialogOpen}
        onOpenChange={setBillingDialogOpen}
        contractId={selectedContract?.id}
        onSuccess={() => {
          void refetch();
          setSelectedContract(null);
        }}
      />

      {/* Stop Billing Period Confirmation Dialog */}
      <AlertDialog open={stopBillingDialogOpen} onOpenChange={setStopBillingDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('areYouSure')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('stopBillingPeriodConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmStopBilling}>
              {stopBillingPeriodMutation.isPending ? t('processing') : t('stopBillingPeriod')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Recalculate Tasks Confirmation Dialog */}
      <AlertDialog open={recalculateDialogOpen} onOpenChange={setRecalculateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('recalculateTasks')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('recalculateTasksConfirm', { name: selectedContract?.name ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRecalculate}
              disabled={recalculateMutation.isPending}
            >
              {recalculateMutation.isPending ? t('processing') : t('recalculateTasks')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
